import ConfigurationSection from 'components/Onboarding/QuickRun/ConfigurationSecton/index';
import Typography from 'components/Atoms/Typography/index';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Controlled as CodeMirror } from 'react-codemirror2';
import TestStatusSteps from '../TestStatusSteps';
import { Icon } from 'components/Icons/index';
import { default as LazyLog } from '../../../../../src/react-lazylog/src/components/LazyLog';
import { emitter } from 'utils/eventEmitter';
import { sendAmplitudeEvents } from 'utils/common';
import { EventNames } from 'utils/events';
import { useSelector } from 'react-redux';
import Loader from 'components/Loader';

// eslint-disable-next-line
import './index.css';
import {
  CLI_RUN_TEST_HYE_GRID_LINK,
  HYE_INTEGRATION_DOC_LINK,
} from 'constants/links';
import { useEffect, useState } from 'react';

const DELAY_FOR_SCROLL = 50;

const CodeMirrorDefaultOption = {
  mode: 'text/x-yaml',
  lineWrapping: true,
  indentWithTabs: false,
  lint: {
    highlightLines: true,
  },
  gutters: ['CodeMirror-lint-markers'],
};
const MaterialOption = {
  ...CodeMirrorDefaultOption,
  theme: 'material',
  lineNumbers: false,
};
const TriggerTestSection = ({
  currentFramework,
  getCommandToRunTest,
  handleAllStepsCompleted,
  handleRetriggerJob,
  setTestStarted,
  testStarted,
  triggerButtonHandler,
}) => {
  const [textCopiedType, setTestCopiedType] = useState('');
  const [testLogs, setTestLogs] = useState('');
  const currentConfiguration = useSelector(
    (state) => state.onboarding?.currentConfigurationQuickRun
  );
  const jobList = useSelector((state) => state.hyperTestJobList?.data?.data);
  const triggerTestStatus = currentConfiguration.triggerTestStatus;

  useEffect(() => {
    if (triggerTestStatus?.logs) {
      setTestLogs(triggerTestStatus?.logs.join('\r\r'));
      // adding timeout for statechange wait, same functionality was not working if we add it in useeffect of dependency [testLogs]
      window.setTimeout(() => {
        const lazyLogInnerContainer = document.querySelector(
          '.ReactVirtualized__Grid__innerScrollContainer'
        );

        lazyLogInnerContainer?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      }, DELAY_FOR_SCROLL);
    }
  }, [triggerTestStatus]);

  const handleTriggerClick = function () {
    setTestStarted(true);
    triggerButtonHandler();
    sendAmplitudeEvents(EventNames.HYP_ONBOARDING_TRIGGER_TEST_BUTTON_CLICK, {
      'testing framework': currentConfiguration.frameworkType,
      language: currentConfiguration?.framework?.language,
      framework: currentConfiguration?.framework?.name,
      concurrency: currentConfiguration?.concurrency,
      timeout: currentConfiguration?.timeout,
      retry: currentConfiguration?.retry,
      isFirstTest: jobList?.length ? true : false,
    });
  };

  const handleClickCLILink = function (doc) {
    sendAmplitudeEvents(EventNames.HYP_ONBOARDING_CLICK_DOC_LINK, {
      'testing framework': currentConfiguration.frameworkType,
      language: currentConfiguration?.framework?.language,
      framework: currentConfiguration?.framework?.name,
      doc,
    });
  };

  const handleClickCiCdLink = function (doc) {
    sendAmplitudeEvents(EventNames.HYP_ONBOARDING_CLICK_DOC_LINK, {
      'testing framework': currentConfiguration.frameworkType,
      language: currentConfiguration?.framework?.language,
      framework: currentConfiguration?.framework?.name,
      doc,
    });
  };

  return (
    <div
      className={`triggertest-section-container ${
        testStarted ? 'triggertest-section-container-activated' : ''
      }`}
    >
      <div
        className={
          'ltw-border-l ltw-border-dashed ltw-border-opacity-40 ltw-border-primary ltw-border-gray-7 ltw-mb-35px completed-step-container'
        }
      >
        <Icon name="completed-step" className="completed-step-icon" />
      </div>
      <ConfigurationSection classname="configration-section-border">
        <>
          <div className="quick-run-content-heading trigger-test-section-heading">
            Trigger your test case
          </div>
          <Typography
            variant="subtext-1"
            color="ltw-text-gray-4"
            className="triggertest-section-subheading"
          >
            This command will be executed to trigger your test. Ideally, you
            will use the{' '}
            <a
              onClick={() => handleClickCLILink('hyperexecute cli')}
              href={CLI_RUN_TEST_HYE_GRID_LINK}
              className="triggertest-section-subheading-link"
              target="_blank"
              rel="noreferrer"
            >
              Hyperexecute CLI
            </a>{' '}
            to run your custom test scenarios through your{' '}
            <a
              onClick={() => handleClickCiCdLink('ci/cd pipeline')}
              href={HYE_INTEGRATION_DOC_LINK}
              target="_blank"
              className="triggertest-section-subheading-link"
              rel="noreferrer"
            >
              CI/CD pipeline
            </a>
            .
          </Typography>
          {!!testStarted && (
            <TestStatusSteps
              handleAllStepsCompleted={handleAllStepsCompleted}
              handleRetriggerJob={handleRetriggerJob}
            />
          )}
          {!testStarted && (
            <button className="trigger-button" onClick={handleTriggerClick}>
              <span className="trigger-button-text">Trigger test</span>
              <Icon name="rocket-icon" />
            </button>
          )}
        </>
        <>
          {testStarted && (
            <>
              <div className="quick-run-logs-header">Hyperexecute CLI logs</div>
              <LazyLog
                enableSearch={false}
                text={testLogs}
                parentEmitter={true}
                parentEventEmitter={emitter}
                caseInsensitive
                selectableLines
                renderScrollTopAndBottom={() => {}}
                rowHeight={22}
                loadingComponent={Loader}
              />
            </>
          )}
          {!testStarted && (
            <div className="hyperexecute-command-container">
              <div className="ltw-absolute ltw-top-15px ltw-right-10px ltw-z-10 ltw-cursor-pointer">
                {textCopiedType !== 'setup' ? (
                  <CopyToClipboard
                    data-tip="Copy"
                    data-for="configure-test"
                    text={currentFramework?.setupGit?.clone}
                    onCopy={() => {
                      setTestCopiedType('setup');
                      window.setTimeout(() => {
                        setTestCopiedType('');
                      }, 1000);
                    }}
                  >
                    <div>
                      <div
                        className="ltw-flex ltw-gap-5px ltw-items-center"
                        data-tip="Copy"
                        data-for="configure-test"
                      >
                        <Icon name="copy-icon" width="14px" height="14px" />
                      </div>
                    </div>
                  </CopyToClipboard>
                ) : (
                  <div className=" ltw-bg-green-color-copied ltw-rounded ltw-py-2px ltw-px-10px ltw-flex ltw-justify-center  ltw-max-w-220px ltw-cursor-pointer">
                    <div className="ltw-flex ltw-justify-center">
                      <Typography
                        variant="subtext-1"
                        color="ltw-text-black-regular"
                        cursor="pointer"
                        fontType="ltw-font-normal"
                      >
                        Copied
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
              <div className="github-clone-code">
                <CodeMirror
                  value={getCommandToRunTest(currentFramework.runTestCommand)}
                  options={MaterialOption}
                  onChange={() => {}}
                />
              </div>
            </div>
          )}
        </>
      </ConfigurationSection>
    </div>
  );
};

export default TriggerTestSection;
